import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { categories } from 'util/appConstants';
import { useAppContext, useFreecodeContext } from 'hooks/useContext';
import ConfirmModal from 'components/Modals/confirmModal';
import { Checkbox } from 'components/Checkbox/Checkbox';
import {
  FreecodeTabContentContainer,
  Title,
  RowContainer,
  ColumnContainer,
  FormSection,
  NumberInput,
  Label,
  InputContainer,
  HiddenFileInput,
} from '../../styles';
import BackgroundColorPicker from './BackgroundColorPicker';
import SheetDataModal from './SheetDataModal';
import {
  LandingPageImage,
  UploadFileButton,
  PlusIcon,
  GraphicsImagesContainer,
  GraphicsImageRow,
  GraphicsImage,
  GraphicsImageCell,
  SheetIcon,
  DeleteIcon,
} from './styles';

export default function FreecodeGraphics() {
  const { isDarkMode } = useAppContext();
  const { test, testImpl } = useFreecodeContext();

  const [graphicsSettings, setGraphicsSettings] = useState({});
  const [graphicsImages, setGraphicsImages] = useState([]);

  const [sheetModalIndex, setSheetModalIndex] = useState(-1);
  const [deleteImageIndex, setDeleteImageIndex] = useState(-1);

  useEffect(() => {
    // TODO use values directly and remove this tmp storage
    if (!testImpl) return;
    setGraphicsSettings(testImpl.configCanvas);
    setGraphicsImages(testImpl.graphics);
  }, [testImpl]);

  const handleNumberInputChange = (parameter, value) => {
    if (value < 0) value = 0;
    setGraphicsSettings({ ...graphicsSettings, [parameter]: value });
  };

  const hiddenFileInputForImage = useRef();
  const hiddenFileInputForBackground = useRef();

  const handleFileUpload = (file, isBackgroundImage = false) => {
    if (!file || !file.type.includes('image')) {
      toast.error('Only image file can be uploaded');
      return;
    }

    //TODO: Implement File Upload
    toast.success('File Upload Not Implemented!');
  };

  const handleSheetDataOnConfirm = (description, jsonData) => {
    let updatedImages = [...graphicsImages];
    if (!updatedImages[sheetModalIndex].jsonData) updatedImages[sheetModalIndex].jsonData = {};
    updatedImages[sheetModalIndex].jsonData = jsonData;
    if (description) updatedImages[sheetModalIndex].description = description;
    setGraphicsImages(updatedImages);

    //TODO: save sheet data!
  };

  const handleDeleteGraphicImage = (graphicImageIndex) => {
    let updatedImages = [...graphicsImages];
    updatedImages.splice(graphicImageIndex, 1);
    setGraphicsImages(updatedImages);
    setDeleteImageIndex(-1);
  };

  const handleCheckboxChange = (option) => {
    let updatedSettings = { ...graphicsSettings };
    if (updatedSettings[option]) {
      switch (option) {
        case 'canvasUsed':
          updatedSettings.canvasUsed = false;
        // falls through
        case 'canvasAnimated':
          updatedSettings.canvasAnimated = false;
        // falls through
        case 'canvasHumanInput':
          updatedSettings.canvasHumanInput = false;
          updatedSettings.canvasHumanAdapter = false;
          updatedSettings.humanInputOwnStepTime = false;
          break;
        default:
          updatedSettings[option] = false;
      }
    } else {
      switch (option) {
        case 'humanInputOwnStepTime':
        // falls through
        case 'canvasHumanAdapter':
          updatedSettings[option] = true;
        // falls through
        case 'canvasHumanInput':
          updatedSettings.canvasHumanInput = true;
        // falls through
        case 'canvasAnimated':
          updatedSettings.canvasAnimated = true;
        // falls through
        default:
          updatedSettings.canvasUsed = true;
      }
    }

    setGraphicsSettings(updatedSettings);
  };

  const renderGraphicsImages = () => {
    if (graphicsImages.length > 0)
      return (
        <GraphicsImagesContainer>
          {graphicsImages.map((image, index) => (
            <GraphicsImageRow key={index}>
              <GraphicsImageCell>
                <GraphicsImage src={image.url} />
              </GraphicsImageCell>
              <GraphicsImageCell overflowHidden>
                <Label hideOverflow title={image.name}>
                  {image.name}
                </Label>
              </GraphicsImageCell>
              <GraphicsImageCell push>
                <SheetIcon
                  darkBlue={image.jsonData && image.jsonData !== ''}
                  title={image.jsonData ? 'Edit sheet' : 'Add sheet'}
                  onClick={() => setSheetModalIndex(index)}
                />
              </GraphicsImageCell>
              <GraphicsImageCell>
                <DeleteIcon onClick={() => setDeleteImageIndex(index)} />
              </GraphicsImageCell>
            </GraphicsImageRow>
          ))}
        </GraphicsImagesContainer>
      );
  };

  return (
    <FreecodeTabContentContainer isDarkMode={isDarkMode}>
      <RowContainer>
        <ColumnContainer>
          <FormSection>
            <Title>Canvas Animation</Title>
            <Checkbox
              isDarkMode={isDarkMode}
              checked={graphicsSettings.canvasUsed}
              label="Uses Canvas"
              onChange={() => handleCheckboxChange('canvasUsed')}
            ></Checkbox>
            <Checkbox
              isDarkMode={isDarkMode}
              checked={graphicsSettings.canvasAnimated}
              label="Has Canvas Animation"
              onChange={() => handleCheckboxChange('canvasAnimated')}
            ></Checkbox>
            <Checkbox
              isDarkMode={isDarkMode}
              checked={graphicsSettings.canvasHumanInput}
              label="Supports Human Input"
              onChange={() => handleCheckboxChange('canvasHumanInput')}
            ></Checkbox>
            <Checkbox
              isDarkMode={isDarkMode}
              checked={graphicsSettings.canvasHumanAdapter}
              label="Human Input using HumanAdapter"
              onChange={() => handleCheckboxChange('canvasHumanAdapter')}
            ></Checkbox>
            <Checkbox
              isDarkMode={isDarkMode}
              checked={graphicsSettings.humanInputOwnStepTime}
              label="Human Input using separate step time"
              onChange={() => handleCheckboxChange('humanInputOwnStepTime')}
            ></Checkbox>
            {graphicsSettings.canvasAnimated && (
              <InputContainer>
                <Label nowrap>Canvas step time (ms):</Label>
                <NumberInput
                  value={graphicsSettings.stepTime}
                  onChange={(e) => handleNumberInputChange('stepTime', e.target.value)}
                ></NumberInput>
              </InputContainer>
            )}
            {graphicsSettings.humanInputOwnStepTime && (
              <InputContainer>
                <Label nowrap>Human Input step time (ms):</Label>
                <NumberInput
                  value={graphicsSettings.stepTimeHumanInput}
                  onChange={(e) => handleNumberInputChange('stepTimeHumanInput', e.target.value)}
                ></NumberInput>
              </InputContainer>
            )}
          </FormSection>
          {graphicsSettings.canvasUsed && (
            <FormSection>
              <Title>Images</Title>
              <Label>Images that can be used in the canvas</Label>
              <UploadFileButton onClick={() => hiddenFileInputForImage.current.click()}>
                <PlusIcon />
              </UploadFileButton>
              <HiddenFileInput
                ref={hiddenFileInputForImage}
                onChange={(e) => handleFileUpload(e.target.files[0])}
              ></HiddenFileInput>
              {renderGraphicsImages()}
            </FormSection>
          )}
        </ColumnContainer>
        <ColumnContainer>
          {graphicsSettings.canvasUsed && (
            <>
              <FormSection>
                <Title>Background Color</Title>
                <Label>Canvas background color where not covered by graphical elements</Label>
                <BackgroundColorPicker
                  color={graphicsSettings.background}
                  onChange={(color) =>
                    setGraphicsSettings({
                      ...graphicsSettings,
                      background: color,
                    })
                  }
                />
              </FormSection>
              <FormSection>
                <Title>Resolution</Title>
                <Label>Canvas aspect ratio and index for graphical element placement</Label>
                <InputContainer>
                  <Label>Width:</Label>
                  <NumberInput
                    value={graphicsSettings.width}
                    onChange={(e) => handleNumberInputChange('width', e.target.value)}
                  ></NumberInput>
                  <Label>Height:</Label>
                  <NumberInput
                    value={graphicsSettings.height}
                    onChange={(e) => handleNumberInputChange('height', e.target.value)}
                  ></NumberInput>
                </InputContainer>
              </FormSection>
            </>
          )}
          {test.category !== categories.CODE.category && (
            <FormSection>
              <Title>Landing Page Image</Title>
              <Label>Default landing page background and thumbnail</Label>
              <UploadFileButton onClick={() => hiddenFileInputForBackground.current.click()}>
                <PlusIcon />
              </UploadFileButton>
              <HiddenFileInput
                ref={hiddenFileInputForBackground}
                onChange={(e) => handleFileUpload(e.target.files[0], true)}
              ></HiddenFileInput>
              <LandingPageImage src={test.backgroundImage?.url || test.backgroundImage?.thumbnail}></LandingPageImage>
            </FormSection>
          )}
        </ColumnContainer>
      </RowContainer>
      {sheetModalIndex > -1 && (
        <SheetDataModal
          graphicsImage={graphicsImages[sheetModalIndex]}
          closeModal={() => setSheetModalIndex(-1)}
          onConfirm={(description, jsonData) => handleSheetDataOnConfirm(description, jsonData)}
        />
      )}
      {deleteImageIndex > -1 && (
        <ConfirmModal
          title="Delete Image"
          text="Are you sure you want to delete this image?"
          onConfirm={() => handleDeleteGraphicImage(deleteImageIndex)}
          onCancel={() => setDeleteImageIndex(-1)}
        />
      )}
    </FreecodeTabContentContainer>
  );
}
